import { lazy, Suspense } from "react";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left.mjs";
import BookOpen from "lucide-react/dist/esm/icons/book-open.mjs";
import Headphones from "lucide-react/dist/esm/icons/headphones.mjs";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw.mjs";
import type { AbridgedDeliveryStatus, ArtworkRef, Briefing, BriefingEntry, ReaderPreferences } from "../types";
import { ArtworkImage } from "./ArtworkImage";

const MarkdownArticle = lazy(() => import("./MarkdownArticle"));

export function BriefingPane({ briefing, delivery, loading, error, online, preferences, busyItemId, root = false, onBack, onReadOriginal, onRetry }: {
  briefing?: Briefing | null;
  delivery?: AbridgedDeliveryStatus;
  loading: boolean;
  error?: string;
  online: boolean;
  preferences: ReaderPreferences;
  busyItemId: string;
  root?: boolean;
  onBack: () => void;
  onReadOriginal: (entry: BriefingEntry) => void;
  onRetry?: () => void;
}) {
  const classes = ["reader-pane", "briefing-pane", root ? "briefing-pane--root" : "", `reader-pane--size-${preferences.typeSize}`, `reader-pane--width-${preferences.width}`, `reader-pane--theme-${preferences.theme}`].filter(Boolean).join(" ");
  const hasDeliveryNotice = Boolean(delivery && !["pending", "ready"].includes(delivery.state));
  return (
    <section className={classes} aria-label={briefing?.title || "Imprint Abridged"}>
      {!root ? <header className="reader-toolbar">
        <button className="reader-toolbar__back" type="button" aria-label="Past editions" onClick={onBack}><ArrowLeft aria-hidden="true" /><span>Past editions</span></button>
        <div className="reader-toolbar__actions"><span className="brief-trust"><Headphones aria-hidden="true" />Listen in Imprint Abridged</span></div>
      </header> : null}
      <div className="reader-scroll">
        {delivery && !error ? <AbridgedDeliveryNotice delivery={delivery} onRetry={onRetry} /> : null}
        {error && briefing ? <InlineRecovery message={error} onRetry={onRetry} /> : null}
        {briefing ? (
          <article className="article-sheet briefing-sheet">
            <header className="article-header">
              <p className="article-kicker">{briefing.format_version === "abridged_v1" ? "Imprint Abridged" : "Earlier Briefing"}</p>
              <h1 tabIndex={-1} data-reader-route-heading>{formatDate(briefing.issue_date)}</h1>
              <p className="article-deck">{briefing.subtitle}</p>
              <p className="article-byline">{briefing.format_version === "abridged_v1" ? `${briefing.entry_count} summarized item${briefing.entry_count === 1 ? "" : "s"}` : `${briefing.entry_count} items`}{briefing.duration_seconds ? ` · ${Math.max(1, Math.round(briefing.duration_seconds / 60))} min listen` : ""}</p>
            </header>
            {briefing.format_version === "legacy_podcast_v1" ? (
              <section className="briefing-entry briefing-entry--legacy">
                <p className="briefing-treatment">Legacy podcast</p>
                <Suspense fallback={<p>Opening the original transcript…</p>}><MarkdownArticle markdown={briefing.visual_markdown || "This legacy episode is available in the private podcast feed."} /></Suspense>
              </section>
            ) : (
              <>
                <section className="briefing-entries" aria-label="In this edition">
                  {briefing.entries.filter((entry) => entry.treatment === "covered").map((entry, index) => <BriefingEntryArticle key={entry.item_id} entry={entry} historical={briefing.format_version !== "abridged_v1"} online={online} busy={busyItemId === entry.item_id} editionArtwork={index === 0 && briefing.artwork_state === "ready" ? briefing.artwork : undefined} onReadOriginal={onReadOriginal} />)}
                </section>
                {briefing.entries.some((entry) => entry.treatment === "read_full") ? (
                  <section className="briefing-read-full" aria-labelledby="briefing-read-full-title">
                    <p className="briefing-treatment">For a focused reading session</p>
                    <h2 id="briefing-read-full-title">Read in Full</h2>
                    {briefing.entries.filter((entry) => entry.treatment === "read_full").map((entry) => <BriefingEntryArticle key={entry.item_id} entry={entry} historical online={online} busy={busyItemId === entry.item_id} onReadOriginal={onReadOriginal} />)}
                  </section>
                ) : null}
              </>
            )}
          </article>
        ) : loading ? <BriefingLoading /> : error ? <InlineRecovery message={error} onRetry={onRetry} /> : hasDeliveryNotice ? null : <p className="reader-state">A new Abridged edition will appear when Imprint finds at least one item it can faithfully summarize.</p>}
      </div>
    </section>
  );
}

function AbridgedDeliveryNotice({ delivery, onRetry }: { delivery: AbridgedDeliveryStatus; onRetry?: () => void }) {
  if (["pending", "ready"].includes(delivery.state)) return null;
  const recoveryTime = formatRecoveryTime(delivery.next_recovery_at);
  const messages: Record<AbridgedDeliveryStatus["state"], string> = {
    pending: "",
    ready: "",
    generating: "Today’s Abridged is being prepared.",
    recovering: "A recovery attempt is preparing today’s Abridged.",
    delayed: recoveryTime
      ? `Today’s Abridged is delayed. Imprint will retry at ${recoveryTime} (${delivery.timezone}).`
      : "Today’s Abridged is delayed and is awaiting its recovery sweep.",
    failed: "Today’s Abridged could not be published. Its fidelity gate stayed closed.",
    no_suitable_items: "Imprint did not find an item it could faithfully summarize today, so it did not pad the edition.",
  };
  const failure = delivery.state === "failed";
  return (
    <aside className={`abridged-delivery abridged-delivery--${delivery.state}`} role={failure ? "alert" : "status"}>
      <strong>{failure ? "Delivery failed" : delivery.state === "no_suitable_items" ? "No edition today" : "Delivery update"}</strong>
      <p>{messages[delivery.state]}</p>
      {failure && onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}
    </aside>
  );
}

function formatRecoveryTime(value: string): string {
  const match = value.match(/T(\d{2}):(\d{2})/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = match[2];
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return "";
  return `${hour % 12 || 12}:${minute} ${hour < 12 ? "AM" : "PM"}`;
}

function BriefingLoading() {
  return (
    <article className="article-sheet briefing-loading-sheet" role="status">
      <span className="sr-only">Opening Imprint Abridged…</span>
      <div className="loading-line loading-line--kicker" aria-hidden="true" />
      <div className="loading-line loading-line--headline" aria-hidden="true" />
      <div className="loading-line loading-line--headline loading-line--headline-short" aria-hidden="true" />
      <div className="loading-line loading-line--meta" aria-hidden="true" />
      <div className="reader-loading-rule" aria-hidden="true" />
      <div className="briefing-loading-entry" aria-hidden="true">
        <div className="loading-line loading-line--kicker" />
        <div className="loading-line loading-line--title" />
        <div className="loading-line loading-line--meta" />
        <div className="loading-line loading-line--body loading-line--wide" />
        <div className="loading-line loading-line--body loading-line--medium" />
      </div>
    </article>
  );
}

function BriefingEntryArticle({ entry, historical, online, busy, editionArtwork, onReadOriginal }: { entry: BriefingEntry; historical: boolean; online: boolean; busy: boolean; editionArtwork?: ArtworkRef | null; onReadOriginal: (entry: BriefingEntry) => void }) {
  const archived = entry.state === "archived";
  const artwork = editionArtwork || (entry.artwork_state === "ready" ? entry.artwork : undefined);
  return (
    <article className={`briefing-entry briefing-entry--${entry.treatment}`}>
      <header>
        {historical ? <p className="briefing-treatment">{entry.treatment === "covered" ? "Covered" : "Read in Full"}</p> : null}
        <h2>{entry.title}</h2>
        <p className="article-byline">{[entry.author_name, entry.site_name].filter(Boolean).join(" · ")}</p>
      </header>
      {entry.treatment === "covered" ? (
        <>
          <p className="briefing-entry__orientation">{entry.orientation}</p>
          {entry.visual_markdown ? <Suspense fallback={null}><MarkdownArticle markdown={entry.visual_markdown} /></Suspense> : null}
          {artwork ? <ArtworkImage artwork={artwork} className="briefing-entry__artwork" ownerItemId={editionArtwork ? undefined : entry.item_id} loading="lazy" /> : null}
        </>
      ) : (
        <>
          <p>{entry.orientation}</p>
          <div className="briefing-why"><strong>Why it deserves your time</strong><p>{entry.why_read}</p></div>
        </>
      )}
      <button className="briefing-read-original" type="button" data-reader-origin-key={`abridged:${entry.item_id}`} disabled={(!online && archived) || busy} onClick={() => onReadOriginal(entry)}>
        {archived ? <RotateCcw aria-hidden="true" /> : <BookOpen aria-hidden="true" />}
        {busy ? "Restoring…" : archived ? "Restore and read original" : "Read original"}
      </button>
    </article>
  );
}

function InlineRecovery({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="reader-state inline-recovery" role="alert"><p>{message}</p>{onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}</div>;
}

function formatDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(date);
}
